import { useDailyOpsRunDownload } from '../../hooks/useDailyOpsRunDownload';
import { useDailyOpsRunRetry } from '../../hooks/useDailyOpsRunRetry';
import { useAuthStore } from '../../store/authStore';
import { formatGasDayLabel } from '../../utils/gasDayLabel';
import type { RunRow } from '../../types/operationsReports';

interface Props {
  run: RunRow;
  showSite: boolean;
  siteName: string;
}

export function DailyOpsRunRow({ run, showSite, siteName }: Props) {
  const { download, isFetching, error, clearError } = useDailyOpsRunDownload();
  const retry = useDailyOpsRunRetry();
  const userRole = useAuthStore((s) => s.user?.role);
  // Retry is gated to platform_admin to mirror the backend
  // /_internal/trigger gate. For all other roles the failed-row Action
  // column shows the muted em-dash as before.
  const canRetry = userRole === 'platform_admin';
  const isFailed = run.status === 'failed';
  const dateLabel = (() => {
    // Derive end date from gas_day_date + 1 day; spec convention is start-date in gas_day_date
    const start = run.gas_day_date;
    const [y, m, d] = start.split('-').map(Number);
    const endDt = new Date(Date.UTC(y, m - 1, d + 1, 12, 0, 0));
    const yy = endDt.getUTCFullYear();
    const mm = String(endDt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(endDt.getUTCDate()).padStart(2, '0');
    return formatGasDayLabel(start, `${yy}-${mm}-${dd}`);
  })();

  return (
    <tr className={isFailed ? 'bg-red-500/5 border-b border-red-500/20' : 'border-b border-gray-100'}>
      <td className="py-2.5 px-1">{dateLabel}</td>
      {showSite && <td className="py-2.5 px-1">{siteName}</td>}
      <td className="py-2.5 px-1">
        {isFailed ? (
          <div>
            <span className="text-red-400 font-medium">⚠ Failed</span>
            {run.error_message && (
              <div
                className="text-xs text-ems-text-muted mt-0.5 italic truncate max-w-xs"
                title={run.error_message}
                aria-label={run.error_message}
              >
                {run.error_message}
              </div>
            )}
          </div>
        ) : (
          <span className="text-emerald-600">✓ Succeeded</span>
        )}
      </td>
      <td className="py-2.5 px-1 text-right align-top">
        {isFailed ? (
          canRetry ? (
            <button
              type="button"
              onClick={() => retry.mutate(run.site_id)}
              disabled={retry.isPending}
              className="text-blue-400 hover:underline font-medium disabled:opacity-50"
            >
              {retry.isPending ? 'Retrying…' : '↻ Retry'}
            </button>
          ) : (
            <span className="text-gray-500">—</span>
          )
        ) : !run.pdf_storage_key ? (
          <span className="text-gray-500">—</span>
        ) : (
          <button
            type="button"
            onClick={() => download(run.run_id)}
            disabled={isFetching}
            className="text-blue-600 hover:underline font-medium disabled:opacity-50"
          >
            {isFetching ? 'Loading…' : '↓ Download PDF'}
          </button>
        )}
        {error && (
          <div className="text-xs text-red-400 mt-1" role="alert">
            {error}{' '}
            <button type="button" onClick={clearError} className="underline">dismiss</button>
          </div>
        )}
        {retry.isError && (
          <div className="text-xs text-red-400 mt-1" role="alert">
            Retry failed: {(retry.error as Error).message}{' '}
            <button type="button" onClick={() => retry.reset()} className="underline">dismiss</button>
          </div>
        )}
      </td>
    </tr>
  );
}
